/**
 * packages/ui/sentences.js — `@citeclerk/ui/sentences`
 *
 * SENTENCE BOUNDARIES IN LEGAL PROSE. Splits on `.?!` followed by whitespace and a capital, EXCEPT
 * where the period belongs to an abbreviation, a citation or an initial — which in this corpus is
 * most of them. `410 U.S. 113`, `Mr. Justice Black`, `42 U.S.C. § 2000e et seq.`, `Pub. L. 88-352`
 * and `Cf. Mich. Comp. Laws` all contain periods and none of them ends a sentence.
 *
 * ── WHERE IT CAME FROM AND WHY IT MOVED ──────────────────────────────────────────────────────
 * RUN AL built this in `ops/run-al-highlights/sentences.mjs` and MEASURED it against two
 * independent hand-read samples of real opinion prose: **5.3% then 5.1% false-boundary rate, and
 * all six errors were inside citation strings** (`10 Pick. ‖ (Mass.)`, `H. R. Rep. ‖ No. 1776`,
 * `Mich. Comp. ‖ Laws`) — **zero in prose**. That is the whole error profile, and it is the
 * profile that decides where this may be used.
 *
 * It moved here, rather than being imported from an ops directory or copied into a component,
 * because `readingBlocks.js`'s covenant applies to it for the same reason: a second copy of a
 * boundary rule drifts, and the drift is invisible.
 *
 * ── WHAT IT IS USED FOR, AND WHY THE ERROR PROFILE IS TOLERABLE THERE ─────────────────────────
 * `spanAnchor.js` uses it to round a located highlight OUT to whole sentences, so a mark never
 * stops mid-clause because a stored excerpt was truncated at 600 characters. Both failure modes
 * are bounded and benign in that job: a FALSE boundary inside a citation makes the mark extend a
 * little less than it could, and a MISSED boundary makes it extend to the next real one. Neither
 * can move the mark off the passage, because the located span is always inside the extension.
 * It is NOT used to decide anything — no span is selected by it, no claim rests on it.
 *
 * ── THE ADDITIONS RUN AM MADE, AND THE RULE THAT REPLACED A LIST ──────────────────────────────
 * RUN AL's note says the fix for the citation-string errors is the abbreviation list, and that it
 * converges fast. Most of it turned out not to need a list at all: **a token immediately preceded
 * by a period is part of a dotted abbreviation** — `U.S.C.`, `e.g.`, `i.e.`, `H.L.`, `C.B.` — and
 * one rule covers the whole family, including the ones nobody has hit yet. The list additions are
 * the statutory vocabulary this run brought into scope, which the opinion-only original had no
 * reason to carry: `subsec`, `subpar`, `seq`, `Amdt`, `Cong`, `Reorg`, `Ord`, `Subch`, `Pt`.
 *
 * ── 🔴 RUN BD — THIS IS NOW *THE* BOUNDARY RULE, AND `boundaryAt` IS WHY IT CAN BE ────────────
 * RECON 5's build blocker 1: `apps/steel/server/lib/sentenceSpan.js` carried a SECOND boundary rule
 * with its own abbreviation set, and the two agreed on only **80.1% of boundaries** over the
 * highlight population (4,760 vs 5,115). The cost was invisible to every coverage metric, because
 * the picker indexes a sentence per THIS file and the display path then rounded the located range
 * out per THAT one: **40.4% of the recommended arm's spans (21 of 52) displayed different text
 * than the sentence the model indexed and the judge cleared** — all 21 grew, and the worst
 * (*Ashcroft v. al-Kidd*) grew into a sentence about a dissent.
 *
 * The collapse could not be "copy the list across", because a copied list is the same defect one
 * commit later. So the position-local half of `segmentSentences`'s loop is extracted as
 * `boundaryAt`, exported, and `sentenceSpan.js` scans with it instead of re-deciding. There is one
 * predicate; `segmentSentences` and `sentenceSpan`'s backward/forward scans are three callers of it.
 *
 * ISOLATION: pure string work. No React, no imports.
 */
const ABBREV = new Set([
  'v', 'vs', 'U.S', 'U. S', 'S', 'Ct', 'L', 'Ed', 'F', 'Supp', 'Cir', 'Fed', 'R', 'Civ', 'P', 'Crim', 'Evid', 'App',
  'Mr', 'Mrs', 'Ms', 'Dr', 'Jr', 'Sr', 'Inc', 'Co', 'Corp', 'Ltd', 'No', 'Nos', 'Op', 'Rev', 'Stat', 'Ann', 'Art',
  'Sec', 'Cl', 'Const', 'Amend', 'Dept', 'Div', 'Dist', 'Ry', 'RR', 'Bros', 'Assn', 'Ass’n', 'Natl', 'Nat',
  'Gen', 'Atty', 'Pub', 'Serv', 'Auth', 'Comm', 'Commn', 'Bd', 'Cty', 'Mun', 'Twp', 'St', 'Ave', 'Cf', 'cf', 'id',
  'Id', 'ibid', 'Ibid', 'ante', 'post', 'supra', 'infra', 'pt', 'ch', 'n', 'nn', 'p', 'pp', 'al', 'etc', 'ex', 'Ex',
  'A', 'B', 'C', 'D', 'E', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'T', 'V', 'W', 'X', 'Y', 'Z',
  'Jan', 'Feb', 'Mar', 'Apr', 'Jun', 'Jul', 'Aug', 'Sep', 'Sept', 'Oct', 'Nov', 'Dec', 'Pa', 'Va', 'Ga', 'Ala', 'Okla',
  'Colo', 'Conn', 'Del', 'Fla', 'Ill', 'Ind', 'Kan', 'Ky', 'Mass', 'Md', 'Mich', 'Minn', 'Miss', 'Mo', 'Mont', 'Neb',
  'Pick', 'Rep', 'Tr', 'Wend', 'Cush', 'Metc', 'Gill', 'Johns', 'Barb', 'Denio', 'How', 'Pet', 'Wall', 'Cranch',
  'Dall', 'Serg', 'Rawle', 'Watts', 'Binn', 'Yeates', 'Whart', 'Paige', 'Sandf', 'Bosw', 'Duer', 'Abb', 'Hun',
  'Misc', 'Bl', 'Q', 'K', 'Ch', 'Eq', 'Exch', 'H.L', 'Cas', 'Camp', 'Esp', 'Stark', 'C.B', 'M', 'W', 'B', 'Ad', 'El',
  'Nev', 'Ore', 'Tenn', 'Tex', 'Vt', 'Wash', 'Wis', 'Wyo', 'Ariz', 'Ark', 'Cal', 'Proc', 'Reg', 'Sess', 'Leg', 'Jud',
  // RUN AM — `Mich. Comp. ‖ Laws` was one of RUN AL's six measured false boundaries.
  'Comp', 'Laws', 'Code', 'Ins', 'Jur', 'Corpus',
  // RUN AM — the statutory vocabulary. The original was measured on opinions only.
  'subsec', 'Subsec', 'subpar', 'Subpar', 'par', 'Par', 'subch', 'Subch', 'Subtit', 'Pt', 'seq',
  'Amdt', 'Cong', 'Reorg', 'Ord', 'Res', 'Enact', 'Eff', 'Repl', 'Cum', 'Supp',
]);

/**
 * isAbbreviationPeriod(text, i) → true when the period at `i` belongs to an abbreviation, an
 * initial or an ellipsis rather than to the end of a sentence. This is the BACKWARD half of the
 * boundary decision, on its own, because the display gate needs exactly it and nothing else: when
 * it trims a leading citation string it has to know that the dot in `v.` or `U. S.` is not a place
 * to cut, while the forward half (a capital must follow) does not apply — a citation run is
 * routinely followed by a bare footnote number.
 *
 * 🔴 IT IS EXPORTED RATHER THAN RE-DERIVED. A second spelling of "which dots are abbreviations" is
 * precisely the defect RUN BD collapsed two files to remove.
 */
export function isAbbreviationPeriod(text, i) {
  const t = String(text || '');
  if (t[i] !== '.') return false;
  const m = /([A-Za-z’']+)$/.exec(t.slice(Math.max(0, i - 12), i));
  const w = m ? m[1] : '';
  if (w && t[i - w.length - 1] === '.') return true;   // a segment of a dotted abbreviation
  if (ABBREV.has(w)) return true;                      // "v." "Inc." "Mr." "subsec."
  if (/^[A-Za-z]$/.test(w)) return true;               // a single-letter initial
  if (t[i - 1] === '.' || t[i + 1] === '.') return true; // an ellipsis
  return false;
}

/**
 * 🔴 boundaryAt(text, i) → { end, next } | null — THE one-position boundary decision.
 *
 * Is the character at `i` a sentence terminator? If so, `end` is the offset just past it and any
 * closing quotes or brackets that belong to the sentence ending there, and `next` is the offset
 * where the following sentence begins (past the separating whitespace). `null` means the character
 * at `i` does not end a sentence — which for a period in this corpus is the common case.
 *
 * Every test in it is POSITION-LOCAL: it reads at most 12 characters back and the whitespace run
 * forward. That is what lets one predicate serve `segmentSentences` (which walks forward
 * accumulating) and `sentenceSpan.js`'s backward scan (which walks back from a located offset)
 * without either re-deciding anything.
 */
export function boundaryAt(text, i) {
  const t = String(text || '');
  const ch = t[i];
  if (ch !== '.' && ch !== '?' && ch !== '!') return null;
  // A closing quote or bracket belongs to the sentence that is ending.
  let j = i + 1;
  while (j < t.length && /["'”’)\]]/.test(t[j])) j += 1;
  // A boundary needs whitespace, then something that can START a sentence.
  let k = j;
  while (k < t.length && /\s/.test(t[k])) k += 1;
  if (k === j) return null;
  if (k < t.length && !/[A-Z“"'(\[\*§]/.test(t[k])) return null;
  /* 🔴 THE DOTTED-ABBREVIATION RULE, WHICH IS WHY THIS IS NOT ONLY A LIST — see
     `isAbbreviationPeriod`. A token whose own first character is preceded by a period is a segment
     of a dotted abbreviation (`U.S.C.`, `e.g.`, `H.L.`, `H. R.`) and no such period ends a
     sentence, which closes the whole family including the members not yet written down. */
  if (ch === '.') {
    if (isAbbreviationPeriod(t, i)) return null;
    if (t[j] === '.') return null;                // an ellipsis is not a terminator
  }
  return { end: j, next: k };
}

/**
 * segmentSentences(text) → [{ start, end, text }] with offsets into the ORIGINAL string.
 * `end` is exclusive of the whitespace that follows the terminator, so the ranges are the
 * sentences themselves and the gaps between them are the separators.
 */
export function segmentSentences(text) {
  const out = [];
  const t = String(text || '');
  let start = 0;
  for (let i = 0; i < t.length; i += 1) {
    const b = boundaryAt(t, i);
    if (!b) continue;
    out.push({ start, end: b.end, text: t.slice(start, b.end) });
    start = b.next;
  }
  if (start < t.length && t.slice(start).trim()) out.push({ start, end: t.length, text: t.slice(start) });
  return out;
}

/**
 * sentenceBounds(text, from, to) → { start, end } — the smallest whole-sentence range covering
 * [from, to). Text with no detectable boundary is one sentence, which is the correct answer for a
 * statutory subsection that runs to a single semicolon-chained period.
 *
 * `sentences` is an optional pre-computed `segmentSentences(text)`. RUN BD added it so a caller
 * that marks several spans in one large opinion can segment once — WITHOUT that caller growing its
 * own copy of the containment rule, which is the defect this whole file exists to prevent.
 */
export function sentenceBounds(text, from, to, sentences_) {
  const src = String(text || '');
  const a = Math.max(0, Math.min(src.length, from));
  const bEnd = Math.max(a, Math.min(src.length, to));
  const sentences = sentences_ || segmentSentences(src);
  if (!sentences.length) return { start: 0, end: src.length };
  let start = null;
  let end = null;
  for (const s of sentences) {
    // Overlap, not containment: the located span routinely begins and ends mid-sentence.
    if (s.end > a && s.start < Math.max(bEnd, a + 1)) {
      if (start === null) start = s.start;
      end = s.end;
    }
  }
  if (start === null) return { start: a, end: bEnd };
  return { start: Math.min(start, a), end: Math.max(end, bEnd) };
}

export default { segmentSentences, sentenceBounds, boundaryAt };
